import { useCallback, useSyncExternalStore } from "react";

export type DashTheme = "dark" | "light";

const STORAGE_KEY = "cr.dash.theme";

// ── Module-level singleton store ──────────────────────────────────────
//
// Multiple components mount this hook (the toggle button, the layout root,
// page-level surfaces). The previous implementation gave each instance its
// own useState, which meant a click only mutated the toggle button's local
// state and the layout never re-rendered to flip data-dash-theme. A custom
// event channel patched the symptom but had side-effects inside a setState
// updater, which React StrictMode runs twice — flaky.
//
// The store below holds one canonical theme value. Every hook instance
// subscribes via useSyncExternalStore, so they all see the same value
// from the same source and re-render together.
// ───────────────────────────────────────────────────────────────────────

function readInitial(): DashTheme {
  if (typeof window === "undefined") return "dark";
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    return stored === "light" ? "light" : "dark";
  } catch {
    return "dark";
  }
}

let currentTheme: DashTheme = readInitial();
const listeners = new Set<() => void>();

// Mirror the current theme onto <html> so portal-rendered elements (toasts,
// popovers, dialogs that escape the dash-v2 wrapper via React portals) stay
// themed too. Without this, the in-dash wrapper goes light but Toasters /
// dropdowns mounted under document.body stay dark (or vice versa), which can
// look like the theme "reverted."
//
// Two things are mirrored, and the `dark` class is the single source of truth:
//   1. The `dark` class — portaled shadcn surfaces resolve their tokens from
//      the root, so a lingering `dark` class on a light dashboard left modals
//      and toasts dark. light ⇒ remove `dark`; dark ⇒ add it.
//   2. data-dash-theme — drives the in-dash light-mode CSS flips.
// The inline no-flash script in index.html applies (1) before first paint;
// this keeps it in sync on every toggle and cross-tab change.
function applyToRoot(theme: DashTheme) {
  if (typeof document === "undefined") return;
  try {
    const root = document.documentElement;
    root.setAttribute("data-dash-theme", theme);
    root.classList.toggle("dark", theme === "dark");
  } catch {
    /* SSR / detached doc — ignore */
  }
}

applyToRoot(currentTheme);

function notify() {
  applyToRoot(currentTheme);
  for (const l of listeners) l();
}

function setStoreTheme(next: DashTheme) {
  if (next !== "light" && next !== "dark") return;
  if (currentTheme === next) return;
  currentTheme = next;
  if (typeof window !== "undefined") {
    try {
      window.localStorage.setItem(STORAGE_KEY, next);
    } catch {
      // localStorage may be disabled (private mode, quota). Theme still
      // works in-memory for the rest of the session — just doesn't persist.
    }
  }
  notify();
}

// Cross-tab sync: one window-level listener, registered exactly once.
if (typeof window !== "undefined" && !(window as unknown as { __crDashThemeBound?: boolean }).__crDashThemeBound) {
  window.addEventListener("storage", (e: StorageEvent) => {
    if (e.key !== STORAGE_KEY) return;
    if (e.newValue !== "light" && e.newValue !== "dark") return;
    if (currentTheme === e.newValue) return;
    currentTheme = e.newValue;
    notify();
  });
  (window as unknown as { __crDashThemeBound?: boolean }).__crDashThemeBound = true;
}

function subscribe(onStoreChange: () => void): () => void {
  listeners.add(onStoreChange);
  return () => {
    listeners.delete(onStoreChange);
  };
}

function getSnapshot(): DashTheme {
  return currentTheme;
}

function getServerSnapshot(): DashTheme {
  return "dark";
}

/**
 * Per-user dashboard theme. Scoped to the dash-v2 wrapper via the
 * `data-dash-theme` attribute · landing pages and the legacy /dashboard
 * surface stay dark regardless. Persists in localStorage.
 */
export function useDashTheme(): {
  theme: DashTheme;
  setTheme: (next: DashTheme) => void;
  toggleTheme: () => void;
} {
  const theme = useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);

  const setTheme = useCallback((next: DashTheme) => {
    setStoreTheme(next);
  }, []);

  const toggleTheme = useCallback(() => {
    setStoreTheme(currentTheme === "dark" ? "light" : "dark");
  }, []);

  return { theme, setTheme, toggleTheme };
}
